/**
 * Single source of truth for resolving a display sender label.
 * Used by ChatBody (getDisplayNames), MessageCard (inbox preview),
 * socketInboxUpdater, and socketMessageDispatcher.
 *
 * Priority: performedBy (DB value) → legacy fallback flags (old messages without performedBy).
 *
 * @param {object} opts
 * @param {string|null} [opts.performedBy]         - DB field: "teamMember"|"workflow"|"broadcast"|"waBizApp"|"api"
 * @param {string|null} [opts.performedByName]     - Display name of the team member (for teamMember sends)
 * @param {string|null} [opts.senderName]          - sendername from message/chat
 * @param {boolean}     [opts.isWorkflowAutomated] - Legacy flag (old messages without performedBy)
 * @param {boolean}     [opts.isBulkMessage]       - Legacy flag (old messages without performedBy)
 * @returns {{ sendername: string|null|undefined, isWorkflowAutomated: boolean }}
 */
export function resolveDisplaySender({
  performedBy,
  performedByName,
  senderName,
  isWorkflowAutomated = false,
  isBulkMessage = false,
} = {}) {
  // performedBy-first: authoritative for all new messages
  if (performedBy === "waBizApp")   return { sendername: senderName || performedByName || "WhatsApp Business App", isWorkflowAutomated: false };
  if (performedBy === "broadcast")  return { sendername: "Broadcast", isWorkflowAutomated: false };
  if (performedBy === "api")        return { sendername: "Partner API", isWorkflowAutomated: false };
  if (performedBy === "workflow")   return { sendername: undefined, isWorkflowAutomated: true };
  if (performedBy === "teamMember") return { sendername: senderName || performedByName || "Agent", isWorkflowAutomated: false };

  // AI agent sends: performedBy carries the agent's display name directly, e.g. "Sales(AI)"
  if (performedBy) return { sendername: performedBy, isWorkflowAutomated: false };

  // Legacy fallbacks — only reached when performedBy is null (old messages pre-migration)
  if (isWorkflowAutomated)          return { sendername: undefined, isWorkflowAutomated: true };
  if (isBulkMessage)                return { sendername: "Broadcast", isWorkflowAutomated: false };

  return { sendername: senderName ?? undefined, isWorkflowAutomated: false };
}
